import type { UseQueryResult } from 'react-query';
import { useQuery } from 'react-query';

import {
  getCurrentSiteFromLocalStorage,
  getUserResources,
  USER_RESOURCES_QUERY_CONFIG,
  USER_RESOURCES_QUERY_KEY,
} from '@hotjar/user-resources';
import type { UserResources } from '@hotjar/user-resources';
import { useCurrentUser } from '@hotjar/user';

// TODO: update imports when moved to the packages
import { queryClient } from '@hotjar/webapp/utils/react-query';
import { SITE_ACCESS_QUERY_KEY } from '@hotjar/webapp/features/Core/Nav/components/SiteDropdown/useSiteAccess';

import type { Site } from '../model';

type Selector = (userResource: UserResources) => Array<Site>;

interface UseSitesProps {
  selector?: Selector;
}

interface UseSitesResponse {
  query: UseQueryResult<Array<Site>, Error>;
  invalidate(): Promise<void>;
}

const defaultSelector: Selector = (userResource) => userResource.sites;

export const useSites = (props?: UseSitesProps): UseSitesResponse => {
  const { selector } = props ?? {};

  const { data } = useCurrentUser();

  const userId = data?.id;

  const invalidate = async () => {
    await Promise.all([
      // invalidate sites
      queryClient.invalidateQueries(USER_RESOURCES_QUERY_KEY),
      // invalidate the call that happens inside main nav site dropdown
      queryClient.invalidateQueries(SITE_ACCESS_QUERY_KEY),
    ]);
  };

  return {
    query: useQuery(USER_RESOURCES_QUERY_KEY, () => getUserResources(userId as number), {
      enabled: !!userId,
      select: typeof selector === 'function' ? selector : defaultSelector,
      placeholderData: () => {
        const currentSite = getCurrentSiteFromLocalStorage();

        if (!currentSite) {
          return undefined;
        }

        return [{ ...currentSite }];
      },
      ...USER_RESOURCES_QUERY_CONFIG,
    }),
    invalidate,
  };
};
